//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  RIGHTS_STATUS,
  RightsRequestCommunicationType,
} from '@apis/rights-management/rightsManagementApiModels';
import { DefaultButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import Spinner, { InlineSpinner } from '@components/atoms/spinner/Spinner';
import { BodyErrorText, CaptionText } from '@components/atoms/text/Text';
import TextAreaInput from '@components/atoms/text-area-input/TextAreaInput';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import Cuid from '@models/Cuid';
import { SocialNetwork } from '@models/SocialNetwork';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import {
  RIGHTS_REQUEST_EXTERNAL_APPROVAL_COMMUNICATION_TYPES,
  RIGHT_STATUSES_READ_ONLY_FORM,
} from '@uia/rights/constants/uiaRightsConstants';
import { useGenerativeAiRightsRequestQuery } from '@uia/rights/hooks/useGenerativeAiRightsRequestQuery';
import { useHasRightsFormErrorsQuery } from '@uia/rights/hooks/useHasRightsFormErrorsQuery';
import { getIsTikTokMentionedRightsEnabled } from '@uia/rights/selectors/getUIARights';
import { getRightsRequestDetailsWithSyndicationCheck } from '@uia/rights/utils/rightsRequestUtils';
import { isDefined } from '@utils/filterPredicates';

import { Locales } from 'app/constants/locales/locale';

const DISALLOWED_STATUSES = [
  RIGHTS_STATUS.INITIATED,
  RIGHTS_STATUS.PENDING,
  RIGHTS_STATUS.EXPIRED,
];
const CHANGE_MESSAGE_DISABLED_STATUSES = [
  RIGHTS_STATUS.NONE,
  RIGHTS_STATUS.FAILED_TO_SEND,
];

export enum RequestTextSource {
  CONFIG = 'CONFIG',
  CUSTOM = 'CUSTOM',
  AI = 'AI',
}

export interface RequestTextProps {
  approvalPhrase: string | undefined;
  characterLimit?: number;
  communicationType: RightsRequestCommunicationType | undefined;
  cuid: Cuid;
  hasMultipleRequestTexts: boolean;
  isDisabled: boolean;
  languageCode?: Locales;
  requestTextSource: RequestTextSource;
  requestType: RightsRequestOptions;
  rightsRequestText: string | undefined;
  setNextRequestText: () => void;
  setRequestTextSource: (source: RequestTextSource) => void;
  setRightsRequestText: (rightsRequestText: string) => void;
}

export const RequestText = ({
  approvalPhrase,
  characterLimit,
  communicationType,
  cuid,
  hasMultipleRequestTexts,
  isDisabled,
  languageCode,
  requestTextSource,
  requestType,
  rightsRequestText = '',
  setNextRequestText,
  setRequestTextSource,
  setRightsRequestText,
}: RequestTextProps) => {
  const [defaultRequestText, setDefaultRequestText] =
    useState(rightsRequestText);

  const isTikTokMentionedRightsEnabled = useSelector(
    getIsTikTokMentionedRightsEnabled,
  );

  const {
    data: metadata,
    isError: isErrorMetadata,
    isPending: isPendingMetadata,
  } = useMediaMetadataQuery(cuid);

  const isTikTokContent = metadata?.socialNetwork === SocialNetwork.TikTok;

  const {
    data: hasFormErrors,
    isError: isErrorFormErrors,
    isPending: isPendingFormErrors,
  } = useHasRightsFormErrorsQuery({
    isTikTokContent,
    languageCode,
    rightsRequestText,
  });

  const { isFetching: isGeneratingText, refetch: onGenerateText } =
    useGenerativeAiRightsRequestQuery({
      approvalPhrase,
      cuid,
      rightsRequestText,
    });

  if (
    isErrorMetadata ||
    isErrorFormErrors ||
    isPendingMetadata ||
    isPendingFormErrors
  ) {
    return null;
  }

  const { status } = getRightsRequestDetailsWithSyndicationCheck(metadata);

  const isAiSource = requestTextSource === RequestTextSource.AI;
  const isFormDisabled = isDisabled || DISALLOWED_STATUSES.includes(status);
  const isChangeMessageAllowed =
    !CHANGE_MESSAGE_DISABLED_STATUSES.includes(status);

  const generateText = async () => {
    setRequestTextSource(RequestTextSource.AI);
    const { data } = await onGenerateText();
    if (isDefined(data)) {
      setDefaultRequestText(rightsRequestText);
      setRightsRequestText(data);
    }
  };

  const isFormReadOnly = RIGHT_STATUSES_READ_ONLY_FORM.includes(status);

  const hasExternalRightsApproval =
    isDefined(communicationType) &&
    RIGHTS_REQUEST_EXTERNAL_APPROVAL_COMMUNICATION_TYPES.includes(
      communicationType,
    ) &&
    status === RIGHTS_STATUS.APPROVED;

  const currentTextLength = rightsRequestText.length;
  const isOverCharacterLimit = characterLimit
    ? currentTextLength > characterLimit
    : false;
  const showCharacterCounter =
    isDefined(characterLimit) && !hasExternalRightsApproval;

  const hasError =
    !hasExternalRightsApproval && (hasFormErrors || isOverCharacterLimit);

  return (
    <>
      <Wrapper hasError={hasError}>
        <TextArea
          hasError={hasError}
          isDisabled={isFormDisabled || isGeneratingText}
          label=''
          onChange={(value) => {
            setRightsRequestText(value);
            setRequestTextSource(RequestTextSource.CUSTOM);
          }}
          value={isGeneratingText ? '' : rightsRequestText}
        />
        {isGeneratingText && <CenteredSpinner />}
        {requestType === RightsRequestOptions.DM &&
          isDefined(metadata.originalUrl) && (
            <DMOriginalUrl>
              <CaptionText>Media URL: {metadata.originalUrl}</CaptionText>
            </DMOriginalUrl>
          )}
        {!isFormReadOnly && (
          <Actions>
            <Trigger
              isDisabled={
                isFormDisabled || isGeneratingText || !isDefined(approvalPhrase)
              }
              onClick={generateText}
              trackingId={UIATrackingIds.RIGHTS_REQUEST_TEXT_GENERATE}
            >
              <Span>
                {isGeneratingText ? (
                  <InlineSpinner />
                ) : (
                  <Icon name='sparkles' />
                )}
                Generate with AI
              </Span>
            </Trigger>
            {isAiSource && (
              <Trigger
                isDisabled={isFormDisabled || isGeneratingText}
                onClick={() => {
                  setRightsRequestText(defaultRequestText);
                  setRequestTextSource(RequestTextSource.CONFIG);
                }}
                trackingId={UIATrackingIds.RIGHTS_REQUEST_TEXT_CLEAR}
              >
                <Span>
                  <Icon name='delete' />
                  Clear
                </Span>
              </Trigger>
            )}
            {hasMultipleRequestTexts &&
              isChangeMessageAllowed &&
              !isAiSource && (
                <Trigger
                  isDisabled={isFormDisabled}
                  onClick={setNextRequestText}
                  trackingId={UIATrackingIds.RIGHTS_REQUEST_TEXT_CHANGE}
                >
                  <Span>
                    <Icon name='sync-alt' />
                    Change Message
                  </Span>
                </Trigger>
              )}
          </Actions>
        )}
      </Wrapper>
      {showCharacterCounter && isTikTokMentionedRightsEnabled && (
        <CharacterCounterContainer>
          <CharacterCount>
            {currentTextLength}/{characterLimit}
          </CharacterCount>
          {isOverCharacterLimit && (
            <ErrorText>
              <StyledIcon name='error' />
              Max {characterLimit} characters allowed
            </ErrorText>
          )}
        </CharacterCounterContainer>
      )}
    </>
  );
};

const Wrapper = styled.div<{ hasError: boolean }>(
  ({ hasError, theme }) => `
    position: relative;
    border: ${theme.borderWidths[2]} solid ${hasError ? theme.colors.aliases.error : theme.colors.gray2};
    border-radius: ${theme.borderRadii[2]};
    background-color: ${theme.colors.gray0};
    font-size: ${theme.fontSizes[5]};
  `,
);

const TextArea = styled(TextAreaInput)<{ hasError?: boolean }>(
  ({ hasError, theme }) => `
    textarea {
      min-height: calc(${theme.spacings[7]} * 2);
      border: none;
      border-radius: ${theme.borderRadii[2]} ${theme.borderRadii[2]} ${theme.borderRadii[0]} ${theme.borderRadii[0]};
    }
    ${
      hasError &&
      `
      textarea:not([disabled]):focus {
        outline: none;
        box-shadow: none;
        border: none;
      }
    `
    }
  `,
);

const DMOriginalUrl = styled.div(
  ({ theme }) => `
  padding: ${theme.spacings[2]} ${theme.spacings[3]};
  background-color: ${theme.colors.gray1};
`,
);

const Actions = styled.div(
  ({ theme }) => `
    display: flex;
    gap: ${theme.spacings[2]};
    flex-wrap: wrap;
    width: 100%;
    padding: ${theme.spacings[2]};
    border-radius: ${theme.borderRadii[2]};
    background-color: ${theme.colors.gray0};
  `,
);

const Trigger = styled(DefaultButton)(
  ({ theme }) => `
    background-color: ${theme.colors.gray0};

    && button[disabled] {
      border: ${theme.borderWidths[1]} solid ${theme.colors.gray2};
      border-radius: ${theme.borderRadii[2]};
    }

    &:hover {
      background-color: ${theme.colors.gray0};
    }
  `,
);

const CenteredSpinner = styled(Spinner)(
  ({ theme }) => `
      position: absolute;
      top: calc(50% - ${theme.spacings[3]} - ${theme.spacings[4]});
      left: calc(50% - ${theme.spacings[3]});
  `,
);

const Span = styled.span(
  ({ theme }) => `
    display: flex;
    align-items: center;
    gap: ${theme.spacings[2]};
  `,
);

const CharacterCounterContainer = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 ${theme.spacings[1]};
  `,
);

const CharacterCount = styled.div(
  ({ theme }) => `
    font-size: ${theme.fontSizes[6]};
    color: ${theme.colors.aliases.bodySecondary};
  `,
);

const ErrorText = styled(BodyErrorText)`
  display: flex;
  justify-content: start;
  align-items: center;
`;

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    margin-right: ${theme.spacings[1]};
`,
);
